import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Award, Download, Share2, ExternalLink, Loader2 } from "lucide-react";
import EnrollmentCertificate, { CERT_W, CERT_H } from "./EnrollmentCertificate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";

// Scales the fixed 1200×850 diploma to its container width.
export const Scaled = ({ children, nodeRef }) => {
  const box = useRef(null);
  const [scale, setScale] = useState(0.3);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / CERT_W));
    ro.observe(el);
    setScale(el.clientWidth / CERT_W);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className="relative overflow-hidden rounded-lg" style={{ aspectRatio: `${CERT_W} / ${CERT_H}` }}>
      <div ref={nodeRef} style={{ width: CERT_W, height: CERT_H, transform: `scale(${scale})`, transformOrigin: "top left" }}>{children}</div>
    </div>
  );
};

export default function CertificateCard({ token }) {
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState("");
  const node = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/certificate/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setCert(data);
        if (data.unseen) axios.post(`${API}/certificate/me/ack`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      })
      .catch(() => setCert(null));
  }, [token]);

  if (!cert) return null;
  const shareUrl = `${window.location.origin}/certificate/${cert.certificate_id}`;
  const fileName = `NextO-Enrollment-Certificate-${cert.certificate_id}.png`;

  const renderPng = async () => {
    await document.fonts.ready;
    return toPng(node.current, { pixelRatio: 2, cacheBust: true, width: CERT_W, height: CERT_H, style: { transform: "none" } });
  };

  const download = async () => {
    setBusy("download");
    try {
      const url = await renderPng();
      const a = document.createElement("a");
      a.href = url; a.download = fileName; a.click();
      toast.success("Certificate saved as PNG.");
    } catch { toast.error("Couldn't render the certificate — try again."); }
    finally { setBusy(""); }
  };

  const share = async () => {
    setBusy("share");
    const text = `${cert.player_name} is officially enrolled at The Next O Technical Academy — ${cert.volume}.`;
    try {
      if (navigator.share) {
        let files;
        try {
          const blob = await (await fetch(await renderPng())).blob();
          const file = new File([blob], fileName, { type: "image/png" });
          if (navigator.canShare?.({ files: [file] })) files = [file];
        } catch { /* image share unsupported — fall back to link */ }
        await navigator.share({ title: "Enrollment Certificate", text, url: shareUrl, ...(files ? { files } : {}) });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Certificate link copied to clipboard.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") { try { await navigator.clipboard.writeText(shareUrl); toast.success("Certificate link copied."); } catch { toast.error("Couldn't share right now."); } }
    } finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-sm transition-colors disabled:opacity-50";
  return (
    <section data-testid="certificate-card" className="mb-10 rounded-2xl border p-5 sm:p-6" style={{ borderColor: `${GOLD}55`, background: "linear-gradient(160deg, rgba(212,175,55,0.08), rgba(10,10,10,0.9))", boxShadow: `0 0 40px ${GOLD}14` }}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Award size={18} style={{ color: GOLD }} />
          <h2 className="font-subheadings text-sm font-bold uppercase tracking-widest">Enrollment Certificate</h2>
          <span data-testid="enrollment-status-badge" className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${GOLD}22`, color: GOLD, border: `1px solid ${GOLD}55` }}>Enrolled</span>
        </div>
        <span className="sm:ml-auto font-mono text-[10px] tracking-[0.25em] uppercase text-white/45">№ {cert.certificate_id}</span>
      </div>
      <Scaled nodeRef={node}>
        <EnrollmentCertificate name={cert.player_name} certId={cert.certificate_id} enrolledAt={cert.enrolled_at} volume={cert.volume} />
      </Scaled>
      <div className="flex flex-wrap gap-2 mt-4">
        <button data-testid="certificate-download-btn" onClick={download} disabled={!!busy} className={`${btn} text-black`} style={{ background: GOLD }}>
          {busy === "download" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PNG
        </button>
        <button data-testid="certificate-share-btn" onClick={share} disabled={!!busy} className={`${btn} border text-white hover:bg-white/10`} style={{ borderColor: `${GOLD}66` }}>
          {busy === "share" ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} Share
        </button>
        <a data-testid="certificate-view-link" href={shareUrl} target="_blank" rel="noreferrer" className={`${btn} text-white/60 hover:text-white`}>
          <ExternalLink size={13} /> Open full size
        </a>
      </div>
    </section>
  );
}
